import { request } from "../../utils/request";
/// <reference path="../task/types.d.ts" />

/**
 * 根据ID获取单个字幕（包含 voice_info）
 * 接口：GET /admin-api/video/subtitles/getById
 * @param subtitleId 字幕ID（当前台词的subtitleId值，来自/video/subtitles/list接口返回的subtitleId字段，如果subtitleId不存在则使用id字段）
 * @returns 字幕详细信息（包含参考音频信息）
 */
export const getSubtitleByIdApi = async (subtitleId: number): Promise<Subtitles.GetSubtitleResponse> => {
  try {
    const response = await request<Task.GetSubtitleByIdResponse>(
      `/video/subtitles/getById`,
      {
        method: "GET",
        params: { id: subtitleId }
      }
    );
    
    if (response.code !== 0 || !response.data) {
      throw new Error(response.msg || '获取字幕详情失败');
    }
    
    // 转换新接口格式（驼峰命名）为旧格式（下划线命名，保持兼容性）
    const subtitleData = response.data;
    const result: Subtitles.GetSubtitleResponse = {
      task_id: subtitleData.taskId,
      origin_text: subtitleData.originText,
      post_text: subtitleData.postText || '',
      post_start_time: subtitleData.postStartTime || '',
      post_end_time: subtitleData.postEndTime || '',
      origin_start_time: subtitleData.originStartTime,
      origin_end_time: subtitleData.originEndTime,
      // origin_post_time: subtitleData.originPostTime, // GetSubtitleResponse 类型中没有此字段
      is_episode_voice: subtitleData.isEpisodeVoice || false,
      origin_audio: subtitleData.originAudio,
      trans_audio: subtitleData.transAudio || '',
      subtitle_id: subtitleData.subtitleId || subtitleData.id,
      score: subtitleData.score,
      audio_speed: subtitleData.audioSpeed || 1.0,
      volume_gain_db: subtitleData.volumeGainDb || 1.0,
      id: subtitleData.id,
      speakers: {
        id: subtitleData.speaker.id,
        series_id: subtitleData.speaker.seriesId,
        task_id: subtitleData.taskId,
        speaker_name: subtitleData.speaker.speakerName,
        description: subtitleData.speaker.description || '',
        is_series_speaker: subtitleData.speaker.isSeriesSpeaker
      },
      voice_info: {
        origin_text: subtitleData.originText,
        origin_start_time: subtitleData.originStartTime,
        origin_end_time: subtitleData.originEndTime,
        origin_audio: subtitleData.originAudio,
        score: subtitleData.score
      },
      words: subtitleData.words.map((word: Task.Words) => ({
        id: word.id,
        subtitle_id: word.subtitleId,
        position: word.position,
        origin_char: word.originChar,
        start_ms: word.startMs,
        end_ms: word.endMs
      }))
      // notes: subtitleData.remark || undefined // GetSubtitleResponse 类型中没有此字段
    };
    
    return result;
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': `/video/subtitles/getById`,
      '字幕ID': subtitleId,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/subtitles/getById?id=${subtitleId}`,
      '请求方法': 'GET'
    };
    
    console.error('=== 获取字幕详情API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 根据说话人ID获取字幕列表
 * @param speakerId 说话人ID
 * @returns 该说话人的所有字幕列表
 * @deprecated 请使用 getChangeToneOptionsApi 替代
 */
export const getSubtitlesBySpeakerApi = (speakerId: number) => {
  return request<Subtitles.GetSubtitlesBySpeakerResponse>(
    `/subtitles/ref/list?speaker_id=${speakerId}`,
    {
      method: "GET",
    }
  );
};

/**
 * 获取换音色选项列表
 * 接口：GET /admin-api/video/subtitles/change-tone-options
 * @param subtitleId 字幕ID（点击台词的subtitleId值）
 * @returns Promise<Subtitles.GetChangeToneOptionsResponse> 换音色选项列表（已合并说话人信息）
 */
export const getChangeToneOptionsApi = async (subtitleId: number): Promise<Subtitles.GetChangeToneOptionsResponse> => {
  try {
    // 并行调用两个接口：获取换音色选项和当前字幕的说话人信息
    const [toneOptionsResponse, subtitleResponse] = await Promise.all([
      request<Subtitles.GetChangeToneOptionsResponse>(
        `/video/subtitles/change-tone-options`,
        {
          method: "GET",
          params: { subtitleId }
        }
      ),
      getSubtitleByIdApi(subtitleId) // 获取当前字幕的说话人信息
    ]);

    if (toneOptionsResponse.code !== 0) {
      throw new Error(toneOptionsResponse.msg || '获取换音色选项失败');
    }

    // 从字幕响应中提取说话人信息
    const speakerInfo = subtitleResponse?.speakers;
    
    // 合并说话人信息到每个音色选项中
    const optionsWithSpeaker = toneOptionsResponse.data.map(option => ({
      ...option,
      speakerName: speakerInfo?.speaker_name || '',
      isSeriesSpeaker: speakerInfo?.is_series_speaker || false,
      description: speakerInfo?.description || null
    }));

    return {
      ...toneOptionsResponse,
      data: optionsWithSpeaker
    };
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': `/video/subtitles/change-tone-options`,
      '字幕ID': subtitleId,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/subtitles/change-tone-options?subtitleId=${subtitleId}`,
      '请求方法': 'GET'
    };
    
    console.error('=== 获取换音色选项API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 根据任务ID查询剧集音色库数据
 * 接口：GET /video/subtitles/query-by-task
 * @param taskId 任务ID
 * @returns Promise<{ code: number, data: Array, msg: string }>
 */
export const queryByTaskApi = async (taskId: number): Promise<{
  code: number;
  data: Array<{
    id: number;
    speakerName: string;
    isSeriesSpeaker: boolean;
    description: string | null;
    seriesId: number;
    subtitleRespVOS: Array<{
      id: number;
      originText: string;
      score: number;
      originAudio: string;
      originStartTime: string;
      originEndTime: string;
    }> | null;
    voiceToneCount: number;
  }>;
  msg: string;
}> => {
  try {
    const response = await request<{
      code: number;
      data: Array<{
        id: number;
        speakerName: string;
        isSeriesSpeaker: boolean;
        description: string | null;
        seriesId: number;
        subtitleRespVOS: Array<{
          id: number;
          originText: string;
          score: number;
          originAudio: string;
          originStartTime: string;
          originEndTime: string;
        }> | null;
        voiceToneCount: number;
      }>;
      msg: string;
    }>(
      `/video/subtitles/query-by-task`,
      {
        method: "GET",
        params: { taskId }
      }
    );

    return response;
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': '/video/subtitles/query-by-task',
      '任务ID': taskId,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/subtitles/query-by-task?taskId=${taskId}`,
      '请求方法': 'GET'
    };
    
    console.error('=== 根据任务ID查询剧集音色库API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 更换音色
 * 接口：PUT /video/subtitles/change-tone
 * @param params 更换音色参数 { subtitleId, selectedSubtitleId, applyToAll }
 * @returns Promise<{ code: number, data: boolean, msg: string }>
 */
export const changeVoiceApi = async (params: {
  subtitleId: number;
  selectedSubtitleId: number;
  applyToAll: boolean;
}): Promise<{ code: number; data: boolean; msg: string }> => {
  try {
    const response = await request<{ code: number; data: boolean; msg: string }>(
      `/video/subtitles/change-tone`,
      {
        method: "PUT",
        body: {
          subtitleId: params.subtitleId,
          selectedSubtitleId: params.selectedSubtitleId,
          applyToAll: params.applyToAll
        }
      }
    );

    return response;
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': '/video/subtitles/change-tone',
      '请求参数': JSON.stringify(params),
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/subtitles/change-tone`,
      '请求方法': 'PUT'
    };
    
    console.error('=== 更换音色API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 更新字幕信息
 * 接口：PUT /video/subtitles/update
 * 请求参数：
 * {
 *   "id": 1,
 *   "originText": "Hello world"
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": { ... },  // 完整的字幕数据（Task.Subtitles格式）
 *   "msg": ""
 * }
 */
export const updateSubtitleApi = async (
  params: { id: number; originText: string }
): Promise<{ code: number; data: Task.Subtitles; msg: string }> => {
  const response = await request<{ code: number; data: Task.Subtitles; msg: string }>(
    `/video/subtitles/update`,
    {
      method: "PUT",
      body: params,
    }
  );
  return response;
};

/**
 * 更新目标语言台词文本（postText）
 * 接口：PUT /admin-api/video/subtitles/update-post-text
 * 实际请求路径：/video/subtitles/update-post-text（前缀由网关/app配置）
 * 请求参数：
 * {
 *   "id": 1024,      // 台词ID
 *   "postText": "修改后的文本"
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 */
export const updatePostTextApi = async (
  params: { id: number; postText: string }
): Promise<{ code: number; data: boolean; msg: string }> => {
  const response = await request<{ code: number; data: boolean; msg: string }>(
    `/video/subtitles/update-post-text`,
    {
      method: "PUT",
      body: params,
    }
  );
  return response;
};

/**
 * 添加留言/备注
 * 接口：PUT /video/subtitles/add-remark
 * 请求参数：
 * {
 *   "subtitleId": 2818,  // 选中台词的id值
 *   "remark": "这是一条备注"
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 */
export const addRemarkApi = async (
  subtitleId: number,
  remark: string
): Promise<{ code: number; data: boolean; msg: string }> => {
  const response = await request<{ code: number; data: boolean; msg: string }>(
    `/video/subtitles/add-remark`,
    {
      method: "PUT",
      body: { subtitleId, remark },
    }
  );
  return response;
};

/**
 * 修改字幕的说话人（视频编辑页）
 * 接口：PUT /admin-api/video/subtitles/change-subtitle-speaker
 * 请求参数：
 * {
 *   "subtitleId": 1,  // 字幕的id的值
 *   "speakerId": 1
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 */
export const changeSubtitleSpeakerApi = async (
  subtitleId: number,
  speakerId: number
): Promise<{ code: number; data: boolean; msg: string }> => {
  const response = await request<{ code: number; data: boolean; msg: string }>(
    `/video/subtitles/change-subtitle-speaker`,
    {
      method: "PUT",
      body: { subtitleId, speakerId },
    }
  );
  return response;
};

/**
 * 重新翻译字幕
 * 接口：POST /admin-api/video/subtitles/translate-one-with-feedback
 * @param subtitleId 字幕ID
 * @param feedback 反馈文本（默认值："维持原语义，贴合场景"）
 * @returns 重新翻译后的字幕数据（转换为 GetSubtitleResponse 格式）
 */
export const retranslateSubtitleApi = async (
  subtitleId: number,
  feedback: string = "维持原语义，贴合场景"
): Promise<Subtitles.RetranslateResponse> => {
  try {
    const url = `/video/subtitles/translate-one-with-feedback`;
    
    // 请求参数（作为查询参数 params 发送）
    const params: Record<string, string> = {
      subtitleId: subtitleId.toString(),
      feedback: feedback
    };
    
    console.log('重新翻译API请求URL:', url);
    console.log('subtitleId:', subtitleId);
    console.log('feedback:', feedback);
    console.log('params:', params);
    
    // 调用新接口，返回新格式（Task.Subtitles）
    const response = await request<Task.GetSubtitleByIdResponse>(
      url,
      {
        method: "POST",
        params: params,
      }
    );
    
    if (response.code !== 0 || !response.data) {
      throw new Error(response.msg || '重新翻译失败');
    }
    
    // 将新接口格式（驼峰命名）转换为旧格式（下划线命名，GetSubtitleResponse）
    const subtitleData = response.data;
    const result: Subtitles.RetranslateResponse = {
      task_id: subtitleData.taskId,
      origin_text: subtitleData.originText,
      post_text: subtitleData.postText || '',
      post_start_time: subtitleData.postStartTime || '',
      post_end_time: subtitleData.postEndTime || '',
      origin_start_time: subtitleData.originStartTime,
      origin_end_time: subtitleData.originEndTime,
      is_episode_voice: subtitleData.isEpisodeVoice || false,
      origin_audio: subtitleData.originAudio,
      trans_audio: subtitleData.transAudio || '',
      subtitle_id: subtitleData.subtitleId || subtitleData.id,
      score: subtitleData.score,
      audio_speed: subtitleData.audioSpeed || 1.0,
      volume_gain_db: subtitleData.volumeGainDb || 1.0,
      id: subtitleData.id,
      speakers: {
        id: subtitleData.speaker.id,
        series_id: subtitleData.speaker.seriesId,
        task_id: subtitleData.taskId,
        speaker_name: subtitleData.speaker.speakerName,
        description: subtitleData.speaker.description || '',
        is_series_speaker: subtitleData.speaker.isSeriesSpeaker
      },
      voice_info: {
        origin_text: subtitleData.originText,
        origin_start_time: subtitleData.originStartTime,
        origin_end_time: subtitleData.originEndTime,
        score: subtitleData.score,
        origin_audio: subtitleData.originAudio
      },
      words: subtitleData.words?.map((word: Task.Words) => ({
        id: word.id,
        subtitle_id: word.subtitleId,
        position: word.position,
        origin_char: word.originChar,
        start_ms: word.startMs,
        end_ms: word.endMs
      })) || []
    };
    
    return result;
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': `/video/subtitles/translate-one-with-feedback`,
      '字幕ID': subtitleId,
      '反馈文本': feedback,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/subtitles/translate-one-with-feedback`,
      '请求方法': 'POST'
    };
    
    console.error('=== 重新翻译API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 删除字幕
 * 接口：DELETE /video/subtitles/delete/{subtitleId}
 * @param subtitleId 选中台词的id值（路径参数）
 * @returns 删除结果
 */
export const deleteSubtitleApi = (subtitleId: number) => {
  return request<{ success: boolean; message: string }>(
    `/video/subtitles/delete/${subtitleId}`,
    {
      method: "DELETE",
    }
  );
};

/**
 * 创建新字幕
 * 接口：POST /admin-api/video/subtitles/create
 * @param params 创建字幕参数（新接口格式：只需要 subtitleId 和 originText）
 * @returns 创建的字幕数据（新接口格式，返回 Task.Subtitles）
 */
export const createSubtitleApi = async (params: Subtitles.CreateSubtitleParams): Promise<Subtitles.CreateSubtitleResponse> => {
  try {
    const response = await request<Subtitles.CreateSubtitleResponse>(
      `/video/subtitles/create`,
      {
        method: "POST",
        body: params,
      }
    );
    
    if (response.code !== 0 || !response.data) {
      throw new Error(response.msg || '创建字幕失败');
    }
    
    return response;
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': `/video/subtitles/create`,
      '参数': params,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/subtitles/create`,
      '请求方法': 'POST'
    };
    
    console.error('=== 创建字幕API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 拆分字幕
 * 接口：POST /admin-api/video/subtitles/split
 * @param params 拆分字幕参数
 * @returns 拆分后的字幕数据（转换为 GetSubtitleResponse 格式数组）
 */
export const splitSubtitleApi = async (params: Subtitles.SplitSubtitleParams): Promise<[Subtitles.GetSubtitleResponse, Subtitles.GetSubtitleResponse]> => {
  try {
    const response = await request<Subtitles.SplitSubtitleResponse>(
      `/video/subtitles/split`,
      {
        method: "POST",
        body: {
          subtitleId: params.subtitleId,
          position: params.position
        }
      }
    );

    if (response.code !== 0 || !response.data || response.data.length !== 2) {
      throw new Error(response.msg || '拆分字幕失败：响应数据格式错误');
    }

    // 将新格式（驼峰命名）转换为旧格式（下划线命名，GetSubtitleResponse）
    const convertToGetSubtitleResponse = (subtitle: Task.Subtitles): Subtitles.GetSubtitleResponse => {
      return {
        task_id: subtitle.taskId,
        origin_text: subtitle.originText,
        post_text: subtitle.postText || '',
        post_start_time: subtitle.postStartTime || '',
        post_end_time: subtitle.postEndTime || '',
        origin_start_time: subtitle.originStartTime,
        origin_end_time: subtitle.originEndTime,
        is_episode_voice: subtitle.isEpisodeVoice || false,
        origin_audio: subtitle.originAudio,
        trans_audio: subtitle.transAudio || '',
        subtitle_id: subtitle.subtitleId || subtitle.id,
        score: subtitle.score,
        audio_speed: subtitle.audioSpeed || 1.0,
        volume_gain_db: subtitle.volumeGainDb || 1.0,
        id: subtitle.id,
        speakers: {
          id: subtitle.speaker.id,
          series_id: subtitle.speaker.seriesId,
          task_id: subtitle.taskId,
          speaker_name: subtitle.speaker.speakerName,
          description: subtitle.speaker.description || '',
          is_series_speaker: subtitle.speaker.isSeriesSpeaker || false
        },
        voice_info: {
          origin_text: subtitle.originText,
          origin_start_time: subtitle.originStartTime,
          origin_end_time: subtitle.originEndTime,
          score: subtitle.score,
          origin_audio: subtitle.originAudio
        },
        words: subtitle.words?.map((word: Task.Words) => ({
          id: word.id,
          subtitle_id: word.subtitleId,
          position: word.position,
          origin_char: word.originChar,
          start_ms: word.startMs,
          end_ms: word.endMs
        })) || []
      };
    };

    return [
      convertToGetSubtitleResponse(response.data[0]),
      convertToGetSubtitleResponse(response.data[1])
    ];
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': `/video/subtitles/split`,
      '字幕ID': params.subtitleId,
      '拆分位置': params.position,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/subtitles/split`,
      '请求方法': 'POST'
    };
    
    console.error('=== 拆分字幕API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 合并字幕
 * 接口：POST /admin-api/video/subtitles/merge
 * @param params 合并字幕参数
 * @returns 合并后的字幕数据（转换为 GetSubtitleResponse 格式）
 */
export const mergeSubtitleApi = async (params: Subtitles.MergeSubtitleParams): Promise<Subtitles.GetSubtitleResponse> => {
  try {
    const response = await request<Subtitles.MergeSubtitleResponse>(
      `/video/subtitles/merge`,
      {
        method: "POST",
        params: {
          subtitleId: params.subtitleId
        }
      }
    );

    if (response.code !== 0 || !response.data) {
      throw new Error(response.msg || '合并字幕失败：响应数据格式错误');
    }

    // 将新格式（驼峰命名）转换为旧格式（下划线命名，GetSubtitleResponse）
    const subtitleData = response.data;
    const result: Subtitles.GetSubtitleResponse = {
      task_id: subtitleData.taskId,
      origin_text: subtitleData.originText,
      post_text: subtitleData.postText || '',
      post_start_time: subtitleData.postStartTime || '',
      post_end_time: subtitleData.postEndTime || '',
      origin_start_time: subtitleData.originStartTime,
      origin_end_time: subtitleData.originEndTime,
      is_episode_voice: subtitleData.isEpisodeVoice || false,
      origin_audio: subtitleData.originAudio,
      trans_audio: subtitleData.transAudio || '',
      subtitle_id: subtitleData.subtitleId || subtitleData.id,
      score: subtitleData.score,
      audio_speed: subtitleData.audioSpeed || 1.0,
      volume_gain_db: subtitleData.volumeGainDb || 1.0,
      id: subtitleData.id,
      speakers: {
        id: subtitleData.speaker.id,
        series_id: subtitleData.speaker.seriesId,
        task_id: subtitleData.taskId,
        speaker_name: subtitleData.speaker.speakerName,
        description: subtitleData.speaker.description || '',
        is_series_speaker: subtitleData.speaker.isSeriesSpeaker || false
      },
      voice_info: {
        origin_text: subtitleData.originText,
        origin_start_time: subtitleData.originStartTime,
        origin_end_time: subtitleData.originEndTime,
        score: subtitleData.score,
        origin_audio: subtitleData.originAudio
      },
      words: subtitleData.words?.map((word: Task.Words) => ({
        id: word.id,
        subtitle_id: word.subtitleId,
        position: word.position,
        origin_char: word.originChar,
        start_ms: word.startMs,
        end_ms: word.endMs
      })) || []
    };

    return result;
  } catch (error: any) {
    const errorInfo = {
      'API调用失败': `/video/subtitles/merge`,
      '字幕ID列表': params.subtitleId,
      '状态码': error?.status || 'N/A',
      '状态文本': error?.statusText || 'N/A',
      '服务器响应': error?.response || 'N/A',
      '错误消息': error?.message || 'N/A',
      '请求URL': `/translate/admin-api/video/subtitles/merge`,
      '请求方法': 'POST'
    };
    
    console.error('=== 合并字幕API调用错误详情 ===');
    console.table(errorInfo);
    console.error('完整错误对象:', error);
    
    throw error;
  }
};

/**
 * 统一调节音量和倍速接口
 * 接口：PUT /video/subtitles/adjust-volume
 * 请求参数：
 * {
 *   "subtitleId": 2822,      // 选中台词的id值，必传
 *   "volumeGainDb": 1.5,     // 必传
 *   "audioSpeed": 1.2,       // 必传
 *   "applyToAll": false      // 必传，如果是调节倍速则固定为false，如果是调节音量则根据用户操作传值
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": [ ... ],  // 数组，包含更新后的字幕数据（Task.Subtitles格式）
 *   "msg": ""
 * }
 */
export const adjustVolumeAndSpeedApi = async (params: {
  subtitleId: number;
  volumeGainDb: number;
  audioSpeed: number;
  applyToAll: boolean;
  postStartTime: string;  // 必传：开始时间戳（调节倍速时需要）
  postEndTime: string;     // 必传：结束时间戳（调节倍速时需要）
}): Promise<{ code: number; data: Task.Subtitles[]; msg: string }> => {
  // 构建请求体
  const body: any = {
    subtitleId: params.subtitleId,
    volumeGainDb: params.volumeGainDb,
    audioSpeed: params.audioSpeed,
    applyToAll: params.applyToAll,
    postStartTime: params.postStartTime,
    postEndTime: params.postEndTime,
  };
  
  const response = await request<{ code: number; data: Task.Subtitles[]; msg: string }>(
    `/video/subtitles/adjust-volume`,
    {
      method: "PUT",
      body,
    }
  );
  return response;
};

/**
 * 调节整条任务的背景音音量
 * 接口：PUT /video/subtitles/adjust-task-volume
 * 请求参数：
 * {
 *   "id": 89,           // 视频任务ID
 *   "volumeGainDb": 1.5 // 音量增益（后端定义的范围和含义）
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": true,
 *   "msg": ""
 * }
 */
export const adjustTaskVolumeApi = async (params: {
  id: number;
  volumeGainDb: number;
}): Promise<{ code: number; data: boolean; msg: string }> => {
  const response = await request<{ code: number; data: boolean; msg: string }>(
    `/video/subtitles/adjust-task-volume`,
    {
      method: "PUT",
      body: {
        id: params.id,
        volumeGainDb: params.volumeGainDb,
      },
    }
  );

  return response;
};

/**
 * 拖动台词调整时间戳范围接口
 * 接口：PUT /video/subtitles/adjust-timestamp-range
 * 请求参数：
 * {
 *   "id": 2818,              // 选中台词的id值
 *   "postStartTime": "00:00:01.234",  // 开始时间戳（格式：HH:mm:ss.SSS）
 *   "postEndTime": "00:00:02.456"      // 结束时间戳（格式：HH:mm:ss.SSS）
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": true,
 *   "msg": ""
 * }
 */
export const adjustTimestampRangeApi = async (params: {
  id: number;
  postStartTime: string;
  postEndTime: string;
}): Promise<{ code: number; data: boolean; msg: string }> => {
  const response = await request<{ code: number; data: boolean; msg: string }>(
    `/video/subtitles/adjust-timestamp-range`,
    {
      method: "PUT",
      body: params,
    }
  );
  return response;
};

/**
 * 一键回译接口
 * 接口：POST /video/subtitles/back-translate
 * 请求参数（查询参数）：
 * taskId: 88  // 任务ID
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": [ ... ],  // 数组，包含回译后的字幕数据（Task.Subtitles格式）
 *   "msg": ""
 * }
 */
export const backTranslateApi = async (taskId: number): Promise<{ code: number; data: Task.Subtitles[]; msg: string }> => {
  const response = await request<{ code: number; data: Task.Subtitles[]; msg: string }>(
    `/video/subtitles/back-translate`,
    {
      method: "POST",
      params: { taskId },
    }
  );
  return response;
};

/**
 * 纯净版校对完成接口
 * 接口：PUT /video/subtitles/proofread
 * 请求参数：
 * {
 *   "taskId": 1
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 */
export const proofreadApi = async (taskId: number): Promise<{ code: number; data: boolean; msg: string }> => {
  const response = await request<{ code: number; data: boolean; msg: string }>(
    `/video/subtitles/proofread`,
    {
      method: "PUT",
      body: { taskId },
    }
  );
  return response;
};

/**
 * 生成视频接口
 * 接口：POST /admin-api/video/subtitles/generate-video
 * 请求参数params格式：
 * {
 *   "taskId": 1
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": false,
 *   "msg": ""
 * }
 */
export const generateVideoApi = async (taskId: number): Promise<{ code: number; data: boolean; msg: string }> => {
  const response = await request<{ code: number; data: boolean; msg: string }>(
    `/video/subtitles/generate-video`,
    {
      method: "POST",
      params: { taskId },
    }
  );
  return response;
};

/**
 * 获取台词历史记录
 * 接口：GET /admin-api/video/subtitles/subtitle-history
 * 请求参数params格式：
 * {
 *   "subtitleId": 1
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": [
 *     {
 *       "id": 0,
 *       "postText": "",
 *       "transAudio": "",
 *       "transAudioTs": "",
 *       "audioSpeed": 0.0,
 *       "volumeGainDb": 0.0
 *     }
 *   ],
 *   "msg": ""
 * }
 */
export const getSubtitleHistoryApi = async (
  subtitleId: number
): Promise<{
  code: number;
  data: {
    id: number;
    postText: string;
    transAudio: string;
    transAudioTs: string;
    audioSpeed: number;
    volumeGainDb: number;
    postStartTime?: string; // 开始时间
    postEndTime?: string; // 结束时间
    selected: boolean; // true-使用中（正在应用）
  }[];
  msg: string;
}> => {
  const response = await request<{
    code: number;
    data: {
      id: number;
      postText: string;
      transAudio: string;
      transAudioTs: string;
      audioSpeed: number;
      volumeGainDb: number;
      postStartTime?: string; // 开始时间
      postEndTime?: string; // 结束时间
      selected: number; // 0-未使用，1-使用中（后端返回）
    }[];
    msg: string;
  }>(`/video/subtitles/subtitle-history`, {
    method: 'GET',
    params: { subtitleId }
  });

  return {
    ...response,
    data: Array.isArray(response.data)
      ? response.data.map((item) => ({
          ...item,
          // 统一前端使用 boolean：true 表示“正在应用”
          selected: Number(item.selected) === 1
        }))
      : []
  };
};

/**
 * 恢复字幕历史记录
 * 接口：POST /admin-api/video/subtitles/restore-history
 * 请求参数params格式：
 * {
 *   "id": 1 // 历史记录ID
 * }
 * 响应数据：
 * {
 *   "code": 0,
 *   "data": { ... },
 *   "msg": ""
 * }
 */
export const restoreSubtitleHistoryApi = async (
  historyId: number
): Promise<{
  code: number;
  data: Subtitles.GetSubtitleResponse;
  msg: string;
}> => {
  const response = await request<{
    code: number;
    data: Task.Subtitles;
    msg: string;
  }>(`/video/subtitles/restore-history`, {
    method: 'POST',
    params: { id: historyId }
  });

  if (response.code !== 0 || !response.data) {
    throw new Error(response.msg || '恢复历史记录失败');
  }

  // 将新格式（驼峰命名）转换为旧格式（下划线命名，GetSubtitleResponse）
  const subtitleData = response.data;
  const result: Subtitles.GetSubtitleResponse = {
    task_id: subtitleData.taskId,
    origin_text: subtitleData.originText,
    post_text: subtitleData.postText || '',
    post_start_time: subtitleData.postStartTime || '',
    post_end_time: subtitleData.postEndTime || '',
    origin_start_time: subtitleData.originStartTime,
    origin_end_time: subtitleData.originEndTime,
    is_episode_voice: subtitleData.isEpisodeVoice || false,
    origin_audio: subtitleData.originAudio,
    trans_audio: subtitleData.transAudio || '',
    subtitle_id: subtitleData.subtitleId || subtitleData.id,
    score: subtitleData.score,
    audio_speed: subtitleData.audioSpeed || 1.0,
    volume_gain_db: subtitleData.volumeGainDb || 1.0,
    id: subtitleData.id,
    speakers: {
      id: subtitleData.speaker.id,
      series_id: subtitleData.speaker.seriesId,
      task_id: subtitleData.taskId,
      speaker_name: subtitleData.speaker.speakerName,
      description: subtitleData.speaker.description || '',
      is_series_speaker: subtitleData.speaker.isSeriesSpeaker || false
    },
    voice_info: {
      origin_text: subtitleData.originText,
      origin_start_time: subtitleData.originStartTime,
      origin_end_time: subtitleData.originEndTime,
      score: subtitleData.score,
      origin_audio: subtitleData.originAudio
    },
    words: subtitleData.words?.map((word: Task.Words) => ({
      id: word.id,
      subtitle_id: word.subtitleId,
      position: word.position,
      origin_char: word.originChar,
      start_ms: word.startMs,
      end_ms: word.endMs
    })) || []
  };

  return {
    code: response.code,
    data: result,
    msg: response.msg
  };
};

/**
 * 调节音量（已废弃，请使用 adjustVolumeAndSpeedApi）
 * @deprecated 请使用 adjustVolumeAndSpeedApi 替代
 */
export const adjustVolumeApi = (taskId: number, params: Subtitles.AdjustVolumeParams) => {
  return request<Subtitles.AdjustVolumeResponse>(
    `/subtitles/update/${taskId}`,
    {
      method: "PUT",
      body: params,
    }
  );
};

/**
 * 重新配音
 * 接口：POST /admin-api/video/subtitles/redub-one
 * @param subtitleId 字幕ID
 * @returns 重新配音后的字幕数据（转换为 GetSubtitleResponse 格式）
 */
export const audioRegenerateApi = async (
  subtitleId: number
): Promise<Subtitles.AudioRegenerateResponse> => {
  try {
    const url = `/video/subtitles/redub-one`;

    // 请求参数（作为查询参数 params 发送）
    const params: Record<string, string> = {
      subtitleId: subtitleId.toString(),
    };

    console.log("重新配音API请求URL:", url);
    console.log("subtitleId:", subtitleId);
    console.log("params:", params);

    // 调用新接口，返回新格式（Task.Subtitles）
    const response = await request<Task.GetSubtitleByIdResponse>(url, {
      method: "POST",
      params,
    });

    if (response.code !== 0 || !response.data) {
      throw new Error(response.msg || "重新配音失败");
    }

    // 将新接口格式（驼峰命名）转换为旧格式（下划线命名，GetSubtitleResponse）
    const subtitleData = response.data;
    const result: Subtitles.AudioRegenerateResponse = {
      task_id: subtitleData.taskId,
      origin_text: subtitleData.originText,
      post_text: subtitleData.postText || "",
      post_start_time: subtitleData.postStartTime || "",
      post_end_time: subtitleData.postEndTime || "",
      origin_start_time: subtitleData.originStartTime,
      origin_end_time: subtitleData.originEndTime,
      is_episode_voice: subtitleData.isEpisodeVoice || false,
      origin_audio: subtitleData.originAudio,
      trans_audio: subtitleData.transAudio || "",
      subtitle_id: subtitleData.subtitleId || subtitleData.id,
      score: subtitleData.score,
      audio_speed: subtitleData.audioSpeed || 1.0,
      volume_gain_db: subtitleData.volumeGainDb || 1.0,
      id: subtitleData.id,
      speakers: {
        id: subtitleData.speaker.id,
        series_id: subtitleData.speaker.seriesId,
        task_id: subtitleData.taskId,
        speaker_name: subtitleData.speaker.speakerName,
        description: subtitleData.speaker.description || "",
        is_series_speaker: subtitleData.speaker.isSeriesSpeaker,
      },
      voice_info: {
        origin_text: subtitleData.originText,
        origin_start_time: subtitleData.originStartTime,
        origin_end_time: subtitleData.originEndTime,
        score: subtitleData.score,
        origin_audio: subtitleData.originAudio,
      },
      words:
        subtitleData.words?.map((word: Task.Words) => ({
          id: word.id,
          subtitle_id: word.subtitleId,
          position: word.position,
          origin_char: word.originChar,
          start_ms: word.startMs,
          end_ms: word.endMs,
        })) || [],
    };

    return result;
  } catch (error: any) {
    const errorInfo = {
      "API调用失败": `/video/subtitles/redub-one`,
      字幕ID: subtitleId,
      状态码: error?.status || "N/A",
      状态文本: error?.statusText || "N/A",
      服务器响应: error?.response || "N/A",
      错误消息: error?.message || "N/A",
      请求URL: `/translate/admin-api/video/subtitles/redub-one`,
      请求方法: "POST",
    };

    console.error("=== 重新配音API调用错误详情 ===");
    console.table(errorInfo);
    console.error("完整错误对象:", error);

    throw error;
  }
};

/**
 * 调节语速
 * @param taskId 任务ID
 * @param params 语速调节参数
 * @returns 调节后的字幕数据
 */
export const adjustSpeedApi = (taskId: number, params: Subtitles.AdjustSpeedParams) => {
  return request<Subtitles.AdjustSpeedResponse>(
    `/subtitles/update/${taskId}`,
    {
      method: "PUT",
      body: params,
    }
  );
};

/**
 * 批量修改音量
 * @param params 批量音量调节参数数组
 * @returns 调节后的字幕数据数组
 */
export const batchAdjustVolumeApi = (params: Subtitles.BatchAdjustVolumeParams[]) => {
  return request<Subtitles.BatchAdjustVolumeResponse>(
    `/subtitles/batch`,
    {
      method: "PUT",
      body: params,
    }
  );
};